import { React, useState, useRef } from 'react';

// mui
import { Typography, Grid, Button, InputAdornment, IconButton, Box, TextField } from '@mui/material';

// icon
import { Visibility, VisibilityOff } from '@mui/icons-material';

// components
import { Footer } from '..';

// api
import { Login } from '../../api';

// client-shared
import { storage } from '@goldblack/client-storage';
import { valid } from '@goldblack/client-utils';
import { BlockedDialog, InactiveDialog, CommonOkDialog } from '@goldblack/client-comps';
import { config } from '@goldblack/client-config';

function LoginBody() {
    const [blockedOpen, setBlockedOpen] = useState(false);
    const [inactiveOpen, setInactiveOpen] = useState(false);
    const [unauthorizeOpen, setUnauthorizeOpen] = useState(false);
    const [expireDate, setExpireDate] = useState('');
    const [disableLogin, setDisableLogin] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const [isShake, setIsShake] = useState(false);
    const emailRef = useRef();
    const passwordRef = useRef();
    const isSubmitting = useRef(false);

    const onLoginSuccess = () => {
        // Navigation is handled by authencated() which sets path.gateway='main'.
        // Do not re-enable the button here; the component will unmount on navigation.
    };

    const onLoginBlocked = (expire) => {
        setBlockedOpen(true);
        setExpireDate(expire.toLocaleString(undefined));
    };

    const onLoginInactive = () => {
        setInactiveOpen(true);
    };

    const onLoginFailed = () => {
        setDisableLogin(false);
        setIsShake(true);
        setTimeout(() => setIsShake(false), 500);
    };

    const onChange = (event) => {
        let emailCheck = valid.checkValidationEmail(emailRef.current.value);
        let passwordCheck = valid.checkValidationPassword(passwordRef.current.value);
        let passwordLengthCheck = passwordRef.current.value.length >= 8;
        setDisableLogin(!(emailCheck && passwordCheck && passwordLengthCheck));
    };

    const onLoginUnauthorized = () => {
        setUnauthorizeOpen(true);
    };

    const onLoginMfaRequired = () => {
        // MFA 검증 페이지로 이동
        storage.transience.setState('path.gateway', 'mfa-verify');
    };

    const onClickLogin = () => {
        const email = emailRef.current.value;
        const password = passwordRef.current.value;
        const configs = config.get();

        setDisableLogin(true);
        Login(
            configs.meta.hash,
            email,
            password,
            import.meta.env.VITE_SERVICE_NAME,
            onLoginSuccess,
            onLoginFailed,
            onLoginBlocked,
            onLoginInactive,
            onLoginMfaRequired,
            onLoginUnauthorized,
        );
    };

    const onClickForgotPassword = () => {};

    const onClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const onClickSignUp = () => {
        storage.transience.setState('path.gateway', 'signup');
    };

    const onCloseBlocked = () => {
        setDisableLogin(false);
        setBlockedOpen(false);
    };

    const onCloseInactive = () => {
        setDisableLogin(false);
        setInactiveOpen(false);
    };

    const onCloseUnauthorize = () => {
        setDisableLogin(false);
        setUnauthorizeOpen(false);
    };

    const onKeyDown = (ev) => {
        if (!disableLogin && ev.key === 'Enter') {
            onClickLogin();
        }
    };

    return (
        <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}>
            <Grid container spacing={0} direction='column' justifyContent='center' alignItems='center'>
                <Typography align='center' variant='m_logo' color='primary.main' sx={{ mb: '0.6em' }}>
                    Loise
                </Typography>
                <Typography
                    align='center'
                    variant='m_overline'
                    color='primary.main'
                    sx={{ mb: 'clamp(32px, 4vh, 64px' }}>
                    administrator tool
                </Typography>
                <Grid container spacing={2} onKeyDown={onKeyDown} sx={{ my: 'clamp(32px, 5vh, 64px)', width: '100%' }}>
                    <Grid size={12}>
                        <TextField
                            required
                            fullWidth
                            autoFocus
                            label='Email Address'
                            name='login-email'
                            autoComplete='off'
                            spellCheck={false}
                            inputRef={emailRef}
                            onChange={onChange}
                        />
                    </Grid>
                    <Grid size={12}>
                        <TextField
                            required
                            fullWidth
                            name='password'
                            label='Password'
                            type={showPassword ? 'text' : 'password'}
                            autoComplete='off'
                            spellCheck={false}
                            inputRef={passwordRef}
                            onChange={onChange}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position='end'>
                                            <IconButton onClick={onClickShowPassword}>
                                                {showPassword ? <Visibility /> : <VisibilityOff />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            sx={{
                                animation: isShake ? 'shake 0.3s ease-in-out' : 'none',
                            }}
                        />
                    </Grid>
                    <Grid size={12}>
                        <Button
                            onClick={onClickLogin}
                            fullWidth
                            variant='contained'
                            size='large'
                            sx={{ mt: 3, mb: 3 }}
                            disabled={disableLogin}>
                            Log In
                        </Button>
                    </Grid>
                </Grid>
                <Footer />

                {/*===== blocked dialog =====*/}
                <BlockedDialog
                    open={blockedOpen}
                    onClose={onCloseBlocked}
                    onOK={onCloseBlocked}
                    expireDate={expireDate}
                />
                <InactiveDialog open={inactiveOpen} onClose={onCloseInactive} onOK={onCloseInactive} />
                <CommonOkDialog
                    open={unauthorizeOpen}
                    onClose={onCloseUnauthorize}
                    onOk={onCloseUnauthorize}
                    title='Access Denied'
                    desc='You do not have permission to log in.'
                />
            </Grid>
        </Box>
    );
}

export { LoginBody };
